import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchAllRowsResult } from '../lib/fetchAll';
import { BUCKETS, bucketsFor } from '../lib/tagBuckets';

/**
 * The chips the filter sheet offers, per bucket, read from the canonical list.
 *
 * WHAT THIS REPLACES, AND WHY IT IS NOT THE SAME QUERY
 *
 * Both filter surfaces used to build their option lists from book_tags:
 *
 *     supabase.from('book_tags').select('tag_type, tropes (canonical_name)')
 *
 * Three things are wrong with that, and all three were live:
 *
 *   1. NO PAGINATION. Supabase caps a response at 1,000 rows whether or not
 *      the code asks for a limit. book_tags is far past that, so both screens
 *      were building the reader's entire filter vocabulary out of an arbitrary
 *      first page and presenting it as the whole catalogue.
 *   2. IT ONLY SEES TAGS ALREADY ON A BOOK. A perfectly good canonical trope
 *      that nothing is tagged with yet is invisible, so it can never be picked
 *      and therefore never gets tagged.
 *   3. FOUR BUCKETS OF EIGHT. It read trope, tone, genre and trigger. Spice,
 *      point of view, setting and representation were not read at all, which
 *      is why those four sections were hardcoded lists marked "coming soon".
 *
 * Reading `tropes` instead fixes all three: it is the canonical vocabulary,
 * it is one row per name rather than one per book, and every bucket is there.
 *
 * WHICH ROWS COUNT AS VOCABULARY
 *
 *   merge_status in ('active', 'pending_review')  and  is_active = true
 *
 * 'merged' rows are dead names that point at a survivor — offering one would
 * filter on a string no book carries any more. 'rejected' is excluded twice
 * over, by name here and by is_active, because rejecting clears is_active and
 * a filter written as is_active alone has excluded rejected rows by accident
 * before rather than on purpose.
 *
 * 'pending_review' MATCHES NOTHING TODAY, AND IS KEPT ON PURPOSE
 *
 * The two conditions are ANDed, so a row only counts if it is BOTH
 * pending_review AND is_active. Every writer of pending_review in this repo
 * leaves the row inactive, so that combination does not currently exist:
 *
 *   process-trope/index.ts:612, :689   inserts is_active: false in the same
 *                                      object as merge_status pending_review
 *   sweep-tropes/index.ts:460          updates merge_status only, but its input
 *                                      set is .eq(is_active,false) (:287, :523),
 *                                      so the row it flags was already inactive
 *
 * and no path anywhere sets is_active true while leaving pending_review in
 * place — every update in TropeQueueSection.jsx that sets is_active true, the
 * auto-publish trigger (20260819020000, which requires
 * merge_status = 'active' before it will publish) and the pov/setting repair
 * (20260905180000:92) all set merge_status = 'active' in the same statement.
 *
 * An earlier version of this comment said pending_review is kept because
 * "books are tagged with it right now". That described a state this query
 * cannot return, and it was right to be called out.
 *
 * DO NOT SIMPLIFY IT AWAY. It is not equivalent to .eq('is_active', true) on
 * its own — it is the explicit, by-name exclusion of 'merged' and 'rejected',
 * and this codebase has already been bitten once by a filter that relied on
 * is_active alone to exclude rejected rows. The pending_review entry is what
 * makes the intent readable: a tag a human has been ASKED about is not thereby
 * invalid, so if a future path ever does publish one while the question is
 * open, it belongs in the reader's vocabulary rather than vanishing from the
 * filter sheet mid-review.
 *
 * FAILURE IS NOT AN EMPTY CATALOGUE
 *
 * This returns { loading, error, vocab }. If the read fails, `error` is set
 * and the sheet says so. The thing it must never do is return eight empty
 * arrays and let the reader conclude that Reanlo has no tropes.
 *
 * VOCAB.GENRE, SINCE PR3 (docs/genre-subgenre-build-spec.md)
 *
 * Every other bucket is still read from `tropes` exactly as described above.
 * genre is the one exception: it now comes from the real `genres` table (20
 * canon rows, PR1's migration) instead of tropes rows carrying
 * category = 'genre'. Those trope rows still exist -- the author form's
 * dual-write into book_tags is deliberately kept until PR5 -- but they are a
 * write path, not the vocabulary a reader should be offered, and reading
 * them here would keep showing whatever subset of the 20 genres happens to
 * already be tagged on a book, the same "can't pick what nothing has been
 * tagged with yet" defect this file's own intro paragraph describes for
 * point of view, setting and representation.
 *
 * If the genres-table read fails, vocab.genre falls back to the tropes-based
 * grouping rather than going empty -- a genuinely empty genres table would
 * be a real outage, but there is no reason a failure reading ONE extra table
 * should blank out a bucket the other read already answered.
 */

const EMPTY_VOCAB = Object.fromEntries(BUCKETS.map(b => [b, []]));

export function useTagVocabulary() {
  const [state, setState] = useState({ loading: true, error: null, vocab: EMPTY_VOCAB });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [tropesResult, genresResult] = await Promise.all([
        fetchAllRowsResult(
          () => supabase
            .from('tropes')
            .select('id, canonical_name, category, usage_count')
            .in('merge_status', ['active', 'pending_review'])
            .eq('is_active', true),
          { orderColumn: 'id' },
        ),
        // Plain select, no pagination: 20 rows, nowhere near the 1,000-row
        // cap fetchAllRowsResult exists for.
        supabase.from('genres').select('name').order('sort_order', { ascending: true }),
      ]);

      if (cancelled) return;

      const { ok, rows, error } = tropesResult;
      if (!ok) {
        setState({ loading: false, error, vocab: EMPTY_VOCAB });
        return;
      }

      const vocab = groupIntoBuckets(rows);

      // See "VOCAB.GENRE, SINCE PR3" above: override the tropes-derived
      // genre bucket with the real 20-genre catalogue. A failure here
      // degrades to the tropes-based list rather than to nothing -- the
      // trope read above already succeeded, so genre going empty on top of
      // that would be a regression this fetch introduced, not a fact about
      // the catalogue.
      if (!genresResult.error && genresResult.data) {
        vocab.genre = genresResult.data.map(g => g.name).filter(Boolean);
      }

      setState({ loading: false, error: null, vocab });
    })();

    return () => { cancelled = true; };
  }, []);

  return state;
}

/**
 * Rows -> { genre: ['Fantasy', ...], trope: [...], ... }
 *
 * Ordered by how often the tag is used, so the chips a reader sees first are
 * the ones that will actually return books, then alphabetically so the order
 * is stable rather than shuffling between loads.
 *
 * usage_count has no NOT NULL and no default on every path, so a null is a
 * count nobody has recorded — not a count of zero. It sorts after the counted
 * rows and before nothing, rather than being read as the least-used tag in the
 * catalogue.
 */
export function groupIntoBuckets(rows) {
  const out = Object.fromEntries(BUCKETS.map(b => [b, []]));
  const seen = Object.fromEntries(BUCKETS.map(b => [b, new Set()]));

  for (const row of rows || []) {
    const name = row?.canonical_name?.trim();
    if (!name) continue;

    // A NAME CAN APPEAR IN SEVERAL SECTIONS, deliberately. `bucketsFor` returns
    // every bucket the name belongs to — the one its category maps to, plus any
    // author-form preset list it is on. So "Dystopian" is offered under Genre
    // and Settings and Tropes, and "First Person" under Point of View as well
    // as wherever its category happens to put it.
    //
    // Filing each name in exactly one section was the earlier version of this,
    // and it was wrong: a name's category is one value shared by every book
    // carrying it, so it can only ever express one of the readings. The others
    // would have been silently dropped.
    for (const bucket of bucketsFor(name, row?.category)) {
      // tropes.canonical_name is UNIQUE and case-SENSITIVE, so "HEA" and "hea"
      // are two rows. Both are real names books are tagged with, so both stay
      // as chips; this only stops the identical string appearing twice.
      if (seen[bucket].has(name)) continue;
      seen[bucket].add(name);

      out[bucket].push({ name, usage: Number.isFinite(row.usage_count) ? row.usage_count : null });
    }
  }

  for (const bucket of BUCKETS) {
    out[bucket].sort((a, b) => {
      if (a.usage === b.usage) return a.name.localeCompare(b.name);
      if (a.usage === null) return 1;
      if (b.usage === null) return -1;
      return b.usage - a.usage;
    });
    out[bucket] = out[bucket].map(t => t.name);
  }

  return out;
}
